import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { stepLibraryApi, productsApi, productWorkflowsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import StepFormBuilder from '../components/StepFormBuilder';
import {
    Plus,
    MagnifyingGlass,
    Pencil,
    Trash,
    FlowArrow,
    Package,
    Cube,
    Copy,
    CheckCircle,
    XCircle,
    Clock,
    FileText,
    Warning,
    ArrowRight,
    Gear,
    TextAlignLeft,
    Robot,
    Sparkle
} from '@phosphor-icons/react';

// Role options
const ROLE_OPTIONS = [
    { value: 'back_office', label: 'Back Office' },
    { value: 'tecnico', label: 'Tecnico' },
    { value: 'progettista', label: 'Progettista' },
    { value: 'installatore', label: 'Installatore' },
    { value: 'amministrazione', label: 'Amministrazione' },
    { value: 'responsabile_operativo', label: 'Responsabile Operativo' },
    { value: 'commerciale', label: 'Commerciale' },
    { value: 'direttore', label: 'Direttore' },
];

// Category options
const STEP_CATEGORIES = [
    { value: 'commerciale', label: 'Commerciale' },
    { value: 'tecnico', label: 'Tecnico' },
    { value: 'amministrativo', label: 'Amministrativo' },
    { value: 'operativo', label: 'Operativo' },
    { value: 'documentale', label: 'Documentale' },
    { value: 'generale', label: 'Generale' },
];

const PRODUCT_CATEGORIES = [
    { value: 'residenziale', label: 'Residenziale' },
    { value: 'commerciale', label: 'Commerciale' },
    { value: 'industriale', label: 'Industriale' },
    { value: 'agrisolare', label: 'Agrisolare' },
    { value: 'comunita_energetica', label: 'Comunità Energetica' },
    { value: 'altro', label: 'Altro' },
];

// ==================== STEP LIBRARY TAB ====================
function StepLibraryTab() {
    const [steps, setSteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [editingStep, setEditingStep] = useState(null);
    const [deleteStep, setDeleteStep] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        assigned_role: 'back_office',
        sla_days: 3,
        is_blocking: true,
        approval_required: false,
        category: 'generale',
        checklist: [],
        required_documents: [],
        optional_documents: []
    });
    const [newRequiredDoc, setNewRequiredDoc] = useState({ code: '', label: '' });
    const [newOptionalDoc, setNewOptionalDoc] = useState({ code: '', label: '' });

    const fetchSteps = useCallback(async () => {
        try {
            const response = await stepLibraryApi.getAll();
            setSteps(response.data || response);
        } catch (error) {
            toast.error('Errore nel caricamento degli step');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSteps();
    }, [fetchSteps]);

    const filteredSteps = steps.filter(step =>
        step.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        step.code?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const resetForm = () => {
        setFormData({
            code: '',
            name: '',
            description: '',
            assigned_role: 'back_office',
            sla_days: 3,
            is_blocking: true,
            approval_required: false,
            category: 'generale',
            checklist: [],
            required_documents: [],
            optional_documents: [],
            form_sections: []
        });
        setNewRequiredDoc({ code: '', label: '', supports_extraction: false });
        setNewOptionalDoc({ code: '', label: '', supports_extraction: false });
    };

    const handleEdit = (step) => {
        setEditingStep(step);
        setFormData({
            code: step.code || '',
            name: step.name || '',
            description: step.description || '',
            assigned_role: step.assigned_role || 'back_office',
            sla_days: step.sla_days || 3,
            is_blocking: step.is_blocking !== false,
            approval_required: step.approval_required || false,
            category: step.category || 'generale',
            checklist: step.checklist || [],
            required_documents: step.required_documents || [],
            optional_documents: step.optional_documents || [],
            form_sections: step.form_sections || []
        });
        setShowDialog(true);
    };

    const handleDuplicate = async (step) => {
        try {
            const duplicateData = {
                ...step,
                code: `${step.code}_COPY`,
                name: `${step.name} (Copia)`
            };
            delete duplicateData.id;
            delete duplicateData._id;
            await stepLibraryApi.create(duplicateData);
            toast.success('Step duplicato con successo');
            fetchSteps();
        } catch (error) {
            toast.error('Errore nella duplicazione dello step');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingStep) {
                await stepLibraryApi.update(editingStep.id, formData);
                toast.success('Step aggiornato con successo');
            } else {
                await stepLibraryApi.create(formData);
                toast.success('Step creato con successo');
            }
            setShowDialog(false);
            setEditingStep(null);
            resetForm();
            fetchSteps();
        } catch (error) {
            toast.error(error.message || 'Errore nel salvataggio dello step');
        }
    };

    const handleDelete = async () => {
        try {
            await stepLibraryApi.delete(deleteStep.id);
            toast.success('Step eliminato');
            setDeleteStep(null);
            fetchSteps();
        } catch (error) {
            toast.error('Errore nell\'eliminazione dello step');
        }
    };

    const addRequiredDoc = () => {
        if (newRequiredDoc.code && newRequiredDoc.label) {
            setFormData(prev => ({
                ...prev,
                required_documents: [...prev.required_documents, { ...newRequiredDoc, mandatory: true, min_files: 1 }]
            }));
            setNewRequiredDoc({ code: '', label: '' });
        }
    };

    const removeRequiredDoc = (index) => {
        setFormData(prev => ({
            ...prev,
            required_documents: prev.required_documents.filter((_, i) => i !== index)
        }));
    };

    const addOptionalDoc = () => {
        if (newOptionalDoc.code && newOptionalDoc.label) {
            setFormData(prev => ({
                ...prev,
                optional_documents: [...prev.optional_documents, { ...newOptionalDoc, mandatory: false }]
            }));
            setNewOptionalDoc({ code: '', label: '' });
        }
    };

    const removeOptionalDoc = (index) => {
        setFormData(prev => ({
            ...prev,
            optional_documents: prev.optional_documents.filter((_, i) => i !== index)
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca step..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="step-search-input"
                        />
                    </div>
                </div>
                <Button 
                    onClick={() => { resetForm(); setEditingStep(null); setShowDialog(true); }}
                    data-testid="create-step-button"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Step
                </Button>
            </div>

            {/* Steps Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSteps.map((step) => (
                    <div
                        key={step.id}
                        className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                        data-testid={`step-card-${step.code}`}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Cube weight="duotone" className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-medium text-sm">{step.name}</h3>
                                    <p className="text-xs text-muted-foreground font-mono">{step.code}</p>
                                </div>
                            </div>
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {step.description || 'Nessuna descrizione'}
                        </p>

                        <div className="flex flex-wrap gap-1.5 mb-3">
                            <Badge variant="outline" className="text-xs">
                                {ROLE_OPTIONS.find(r => r.value === step.assigned_role)?.label || step.assigned_role}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {step.sla_days}g
                            </Badge>
                            {step.is_blocking && (
                                <Badge variant="destructive" className="text-xs">Bloccante</Badge>
                            )}
                            {step.approval_required && (
                                <Badge className="text-xs bg-amber-500">Approvazione</Badge>
                            )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            {step.required_documents?.length > 0 && (
                                <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3" />
                                    {step.required_documents.length} doc
                                </span>
                            )}
                        </div>

                        <div className="flex gap-2 pt-3 border-t border-border">
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="flex-1"
                                onClick={() => handleEdit(step)}
                                data-testid={`edit-step-${step.code}`}
                            >
                                <Pencil className="w-4 h-4 mr-1" />
                                Modifica
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDuplicate(step)}
                                data-testid={`duplicate-step-${step.code}`}
                            >
                                <Copy className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setDeleteStep(step)}
                                data-testid={`delete-step-${step.code}`}
                            >
                                <Trash className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                ))}
            </div>

            {filteredSteps.length === 0 && (
                <div className="text-center py-12">
                    <Cube className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">Nessuno step trovato</h3>
                    <p className="text-muted-foreground mb-4">
                        {searchQuery ? 'Prova a modificare i criteri di ricerca' : 'Crea il primo step della libreria'}
                    </p>
                    {!searchQuery && (
                        <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                            <Plus className="w-4 h-4 mr-2" />
                            Crea Step
                        </Button>
                    )}
                </div>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {editingStep ? 'Modifica Step' : 'Nuovo Step'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingStep ? 'Modifica le proprietà dello step' : 'Crea un nuovo step riutilizzabile nella libreria'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <Tabs defaultValue="properties" className="flex-1 flex flex-col overflow-hidden">
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="properties" className="gap-1">
                                <Gear className="w-4 h-4" />
                                Proprietà
                            </TabsTrigger>
                            <TabsTrigger value="documents" className="gap-1">
                                <FileText className="w-4 h-4" />
                                Documenti
                            </TabsTrigger>
                            <TabsTrigger value="form" className="gap-1">
                                <TextAlignLeft className="w-4 h-4" />
                                Form
                            </TabsTrigger>
                        </TabsList>
                        
                        <div className="flex-1 overflow-y-auto mt-4">
                            {/* Properties Tab */}
                            <TabsContent value="properties" className="space-y-4 m-0">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="code">Codice Step *</Label>
                                        <Input
                                            id="code"
                                            value={formData.code}
                                            onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                                            placeholder="ES: SOPRALLUOGO"
                                            required
                                            data-testid="step-code-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="name">Nome Step *</Label>
                                        <Input
                                            id="name"
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Sopralluogo"
                                            required
                                            data-testid="step-name-input"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Descrizione</Label>
                                    <Textarea
                                        id="description"
                                        value={formData.description}
                                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                        placeholder="Descrizione dello step..."
                                        rows={2}
                                        data-testid="step-description-input"
                                    />
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label>Ruolo Responsabile</Label>
                                        <Select
                                            value={formData.assigned_role}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, assigned_role: value }))}
                                        >
                                            <SelectTrigger data-testid="step-role-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {ROLE_OPTIONS.map(role => (
                                                    <SelectItem key={role.value} value={role.value}>
                                                        {role.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Categoria</Label>
                                        <Select
                                            value={formData.category}
                                            onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                                        >
                                            <SelectTrigger data-testid="step-category-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {STEP_CATEGORIES.map(cat => (
                                                    <SelectItem key={cat.value} value={cat.value}>
                                                        {cat.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="sla_days">SLA (giorni)</Label>
                                        <Input
                                            id="sla_days"
                                            type="number"
                                            min="1"
                                            value={formData.sla_days}
                                            onChange={(e) => setFormData(prev => ({ ...prev, sla_days: parseInt(e.target.value) || 1 }))}
                                            data-testid="step-sla-input"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-6 p-3 bg-muted/30 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="is_blocking"
                                            checked={formData.is_blocking}
                                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_blocking: checked }))}
                                            data-testid="step-blocking-checkbox"
                                        />
                                        <Label htmlFor="is_blocking" className="text-sm">Bloccante</Label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Checkbox
                                            id="approval_required"
                                            checked={formData.approval_required}
                                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, approval_required: checked }))}
                                            data-testid="step-approval-checkbox"
                                        />
                                        <Label htmlFor="approval_required" className="text-sm">Richiede Approvazione</Label>
                                    </div>
                                </div>
                            </TabsContent>

                            {/* Documents Tab */}
                            <TabsContent value="documents" className="space-y-6 m-0">
                                {/* Required Documents */}
                                <div className="space-y-3">
                                    <Label className="text-base font-medium">Documenti Obbligatori</Label>
                                    <div className="flex gap-2 flex-wrap">
                                        <Input
                                            value={newRequiredDoc.code}
                                            onChange={(e) => setNewRequiredDoc(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                                            placeholder="Codice"
                                            className="w-32"
                                            data-testid="req-doc-code-input"
                                        />
                                        <Input
                                            value={newRequiredDoc.label}
                                            onChange={(e) => setNewRequiredDoc(prev => ({ ...prev, label: e.target.value }))}
                                            placeholder="Nome documento"
                                            className="flex-1 min-w-[180px]"
                                            data-testid="req-doc-label-input"
                                        />
                                        <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-violet-50/50 border-violet-200">
                                            <Robot className="w-4 h-4 text-violet-600" />
                                            <span className="text-xs text-violet-700">AI</span>
                                            <Switch
                                                checked={newRequiredDoc.supports_extraction || false}
                                                onCheckedChange={(checked) => setNewRequiredDoc(prev => ({ ...prev, supports_extraction: checked }))}
                                            />
                                        </div>
                                        <Button type="button" variant="outline" onClick={addRequiredDoc}>
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    {formData.required_documents.length > 0 && (
                                        <div className="space-y-2">
                                            {formData.required_documents.map((doc, index) => (
                                                <div key={doc.code || `req-${index}`} className="flex items-center gap-2 p-2 bg-destructive/5 border border-destructive/20 rounded">
                                                    <code className="text-xs font-mono text-muted-foreground">{doc.code}</code>
                                                    <span className="flex-1 text-sm">{doc.label}</span>
                                                    {doc.supports_extraction && (
                                                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300 flex items-center gap-1">
                                                            <Robot className="w-3 h-3" />
                                                            Estrazione AI
                                                        </Badge>
                                                    )}
                                                    <Badge variant="destructive" className="text-xs">Obbligatorio</Badge>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeRequiredDoc(index)}
                                                        className="h-6 w-6 p-0 text-destructive"
                                                    >
                                                        <Trash className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Optional Documents */}
                                <div className="space-y-3">
                                    <Label className="text-base font-medium">Documenti Opzionali</Label>
                                    <div className="flex gap-2 flex-wrap">
                                        <Input
                                            value={newOptionalDoc.code}
                                            onChange={(e) => setNewOptionalDoc(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                                            placeholder="Codice"
                                            className="w-32"
                                            data-testid="opt-doc-code-input"
                                        />
                                        <Input
                                            value={newOptionalDoc.label}
                                            onChange={(e) => setNewOptionalDoc(prev => ({ ...prev, label: e.target.value }))}
                                            placeholder="Nome documento"
                                            className="flex-1 min-w-[180px]"
                                            data-testid="opt-doc-label-input"
                                        />
                                        <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-violet-50/50 border-violet-200">
                                            <Robot className="w-4 h-4 text-violet-600" />
                                            <span className="text-xs text-violet-700">AI</span>
                                            <Switch
                                                checked={newOptionalDoc.supports_extraction || false}
                                                onCheckedChange={(checked) => setNewOptionalDoc(prev => ({ ...prev, supports_extraction: checked }))}
                                            />
                                        </div>
                                        <Button type="button" variant="outline" onClick={addOptionalDoc}>
                                            <Plus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    {formData.optional_documents.length > 0 && (
                                        <div className="space-y-2">
                                            {formData.optional_documents.map((doc, index) => (
                                                <div key={doc.code || `opt-${index}`} className="flex items-center gap-2 p-2 bg-muted/30 border rounded">
                                                    <code className="text-xs font-mono text-muted-foreground">{doc.code}</code>
                                                    <span className="flex-1 text-sm">{doc.label}</span>
                                                    {doc.supports_extraction && (
                                                        <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-300 flex items-center gap-1">
                                                            <Robot className="w-3 h-3" />
                                                            Estrazione AI
                                                        </Badge>
                                                    )}
                                                    <Badge variant="outline" className="text-xs">Opzionale</Badge>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeOptionalDoc(index)}
                                                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                                                    >
                                                        <Trash className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            {/* Form Tab */}
                            <TabsContent value="form" className="m-0">
                                <StepFormBuilder
                                    sections={formData.form_sections || []}
                                    onChange={(sections) => setFormData(prev => ({ ...prev, form_sections: sections }))}
                                />
                            </TabsContent>
                        </div>
                    </Tabs>

                    <DialogFooter className="mt-4 pt-4 border-t">
                        <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                            Annulla
                        </Button>
                        <Button onClick={handleSubmit} data-testid="save-step-button">
                            {editingStep ? 'Salva Modifiche' : 'Crea Step'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteStep} onOpenChange={() => setDeleteStep(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Elimina Step</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sei sicuro di voler eliminare lo step "{deleteStep?.name}"? 
                            Questa azione non può essere annullata.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ==================== PRODUCTS TAB ====================
function ProductsTab() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDialog, setShowDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [deleteProduct, setDeleteProduct] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        description: '',
        category: 'residenziale',
        is_active: true
    });

    const fetchProducts = useCallback(async () => {
        try {
            const response = await productsApi.getAll();
            const data = response.data || response;
            // Get workflow count for each product
            const productsWithWorkflows = await Promise.all(
                data.map(async (product) => {
                    try {
                        const wfResponse = await productWorkflowsApi.getAll(product.id);
                        const workflows = wfResponse.data || wfResponse;
                        return { ...product, workflow_count: workflows.length };
                    } catch {
                        return { ...product, workflow_count: 0 };
                    }
                })
            );
            setProducts(productsWithWorkflows);
        } catch (error) {
            toast.error('Errore nel caricamento dei prodotti');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const filteredProducts = products.filter(product =>
        product.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.code?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const resetForm = () => {
        setFormData({
            code: '',
            name: '',
            description: '',
            category: 'residenziale',
            is_active: true
        });
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        setFormData({
            code: product.code || '',
            name: product.name || '',
            description: product.description || '',
            category: product.category || 'residenziale',
            is_active: product.is_active !== false
        });
        setShowDialog(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingProduct) {
                await productsApi.update(editingProduct.id, formData);
                toast.success('Prodotto aggiornato con successo');
            } else {
                await productsApi.create(formData);
                toast.success('Prodotto creato con successo');
            }
            setShowDialog(false);
            setEditingProduct(null);
            resetForm();
            fetchProducts();
        } catch (error) {
            toast.error(error.message || 'Errore nel salvataggio del prodotto');
        }
    };

    const handleDelete = async () => {
        try {
            await productsApi.delete(deleteProduct.id);
            toast.success('Prodotto eliminato');
            setDeleteProduct(null);
            fetchProducts();
        } catch (error) {
            toast.error('Errore nell\'eliminazione del prodotto');
        }
    };

    const goToWorkflowBuilder = (product) => {
        navigate(`/workflow-console/product/${product.id}`);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                    <div className="relative flex-1 max-w-md">
                        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca prodotti..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                            data-testid="product-search-input"
                        />
                    </div>
                </div>
                <Button 
                    onClick={() => { resetForm(); setEditingProduct(null); setShowDialog(true); }}
                    data-testid="create-product-button"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Prodotto
                </Button>
            </div>

            {/* Products Table */}
            <div className="bg-white border border-border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Prodotto</TableHead>
                            <TableHead>Codice</TableHead>
                            <TableHead>Categoria</TableHead>
                            <TableHead>Workflow</TableHead>
                            <TableHead>Stato</TableHead>
                            <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredProducts.map((product) => (
                            <TableRow key={product.id} data-testid={`product-row-${product.code}`}>
                                <TableCell>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                                            <Package weight="duotone" className="w-5 h-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{product.name}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-1">
                                                {product.description || 'Nessuna descrizione'}
                                            </p>
                                        </div>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    <code className="text-xs bg-muted px-2 py-1 rounded">
                                        {product.code}
                                    </code>
                                </TableCell>
                                <TableCell>
                                    <Badge variant="outline">
                                        {PRODUCT_CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                                    </Badge>
                                </TableCell>
                                <TableCell>
                                    <div className="flex items-center gap-2">
                                        <FlowArrow className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm">{product.workflow_count || 0} workflow</span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {product.is_active ? (
                                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                            <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
                                            Attivo
                                        </Badge>
                                    ) : (
                                        <Badge variant="secondary">
                                            <XCircle weight="fill" className="w-3 h-3 mr-1" />
                                            Inattivo
                                        </Badge>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => goToWorkflowBuilder(product)}
                                            data-testid={`workflow-btn-${product.code}`}
                                        >
                                            <FlowArrow className="w-4 h-4 mr-1" />
                                            Workflow
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleEdit(product)}
                                            data-testid={`edit-product-${product.code}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-destructive hover:text-destructive"
                                            onClick={() => setDeleteProduct(product)}
                                            data-testid={`delete-product-${product.code}`}
                                        >
                                            <Trash className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {filteredProducts.length === 0 && (
                    <div className="text-center py-12">
                        <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <h3 className="text-lg font-medium mb-2">Nessun prodotto trovato</h3>
                        <p className="text-muted-foreground mb-4">
                            {searchQuery ? 'Prova a modificare i criteri di ricerca' : 'Crea il primo prodotto'}
                        </p>
                        {!searchQuery && (
                            <Button onClick={() => { resetForm(); setShowDialog(true); }}>
                                <Plus className="w-4 h-4 mr-2" />
                                Crea Prodotto
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>
                            {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingProduct ? 'Modifica le proprietà del prodotto' : 'Crea un nuovo prodotto con il relativo workflow'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="product_code">Codice Prodotto *</Label>
                                <Input
                                    id="product_code"
                                    value={formData.code}
                                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s/g, '_') }))}
                                    placeholder="ES: FV_STANDARD"
                                    required
                                    data-testid="product-code-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Categoria</Label>
                                <Select
                                    value={formData.category}
                                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                                >
                                    <SelectTrigger data-testid="product-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PRODUCT_CATEGORIES.map(cat => (
                                            <SelectItem key={cat.value} value={cat.value}>
                                                {cat.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="product_name">Nome Prodotto *</Label>
                            <Input
                                id="product_name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Impianto Fotovoltaico Standard"
                                required
                                data-testid="product-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="product_description">Descrizione</Label>
                            <Textarea
                                id="product_description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Descrizione del prodotto..."
                                rows={3}
                                data-testid="product-description-input"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="product_active"
                                checked={formData.is_active}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                                data-testid="product-active-checkbox"
                            />
                            <Label htmlFor="product_active" className="text-sm">Prodotto Attivo</Label>
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                                Annulla
                            </Button>
                            <Button type="submit" data-testid="save-product-button">
                                {editingProduct ? 'Salva Modifiche' : 'Crea Prodotto'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteProduct} onOpenChange={() => setDeleteProduct(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Elimina Prodotto</AlertDialogTitle>
                        <AlertDialogDescription>
                            Sei sicuro di voler eliminare il prodotto "{deleteProduct?.name}"? 
                            Questa azione eliminerà anche tutti i workflow associati.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Elimina
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ==================== MAIN CONSOLE ====================
export default function WorkflowConsole() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'products';

    const handleTabChange = (value) => {
        setSearchParams({ tab: value });
    };

    return (
        <div className="space-y-6" data-testid="workflow-console">
            {/* Console Header */}
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center">
                    <FlowArrow weight="fill" className="w-6 h-6 text-white" />
                </div>
                <div>
                    <h1 className="text-2xl font-heading font-bold">Workflow Builder</h1>
                    <p className="text-muted-foreground">
                        Console di configurazione prodotti e step
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
                <TabsList className="grid w-full max-w-md grid-cols-2">
                    <TabsTrigger value="products" data-testid="tab-products" className="gap-2">
                        <Package className="w-4 h-4" />
                        Prodotti
                    </TabsTrigger>
                    <TabsTrigger value="step-library" data-testid="tab-step-library" className="gap-2">
                        <Cube className="w-4 h-4" />
                        Step Library
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="products" className="mt-6">
                    <ProductsTab />
                </TabsContent>

                <TabsContent value="step-library" className="mt-6">
                    <StepLibraryTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}
